/**
 * What every plan pane draws when the 3D renderer's chunk could not be loaded (a deploy that
 * replaced the chunk under an open tab, a dropped connection): `spec-onboarding-3d-plan-konva-
 * removal`, R4. Each pane shows renderer-switch's failed state with the `load` cause instead of a
 * plan, in the same bottom-centre place (`PlanDrawStatusBanner`); the Observers corner plan shows
 * the title alone. No Retry: drawing again cannot fetch a chunk that failed, only a reload can.
 *
 * In the page's own chunk, beside `PlanRendererProviders`, so it is there when the 3D chunk is not.
 */
import styled from 'styled-components';
import {
  PlannerCanvasRendererProvider,
  ReadOnlyCanvasRendererProvider,
  useDrawnReport,
} from '@repo/ui/planner';
import type { ReadOnlyCanvasProps } from '@repo/ui/planner';
import { PlanDrawStatusBanner } from './PlanDrawStatusBanner';
import { planDrawStatusView } from './planDrawStatusView';
import { PlanPictureRendererContext } from './planPictureRenderer';
import { useEffect } from 'react';
import {
  OPENING_VIEW_PICTURE_FAILED_LOG,
  PlanOpeningViewRendererContext,
  PlanPlacementRendererContext,
  type OpeningViewPictureProps,
} from './planPlacementRenderer';
import type { PlanSceneProvidersProps } from './PlanSceneProviders';
import type { PlanStageProps } from './PlanStage';

const LOAD_FAILED = { kind: 'failed', cause: 'load' } as const;

/** The pane the banner is placed in: the whole of the canvas's slot. */
const Pane = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

/**
 * Position twin's map: the whole window under the glass (`onboarding-persistent-scene-position-twin`,
 * R2), so its failed banner is an overlay over the page, as the live canvas's is. It takes no
 * pointer but the banner's own.
 */
const PlacementPane = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  [data-plan-draw-status-host] {
    pointer-events: auto;
  }
`;

/**
 * A pane that has failed from its first frame, and tells its host so, as a 3D pane that cannot draw
 * does: a host waiting on the plan (calibration's loading block) stops waiting.
 */
const loadFailedPane = (compact: boolean) =>
  function PlanLoadFailedPane({
    onDrawFailedChange,
  }: Pick<ReadOnlyCanvasProps, 'onDrawFailedChange'>) {
    useDrawnReport(true, onDrawFailedChange);
    return (
      <Pane data-testid="plan-load-failed">
        <PlanDrawStatusBanner view={planDrawStatusView(LOAD_FAILED, { elapsedMs: 0, compact })} />
      </Pane>
    );
  };

/** Every plan pane, editable or read-only: the failed banner with the `load` body. */
const PlanLoadFailedCanvas = loadFailedPane(false);

/** Position twin's map: the same, over the whole window. */
const PlanLoadFailedPlacement = () => (
  <PlacementPane data-testid="plan-load-failed">
    <PlanDrawStatusBanner
      view={planDrawStatusView(LOAD_FAILED, { elapsedMs: 0, compact: false })}
    />
  </PlacementPane>
);

/** Position on map's opening view picture: it cannot be drawn, said at once. */
const PlanLoadFailedOpeningViewPicture = ({ request, onResult }: OpeningViewPictureProps) => {
  useEffect(() => {
    console.warn(OPENING_VIEW_PICTURE_FAILED_LOG, { reason: 'load' });
    onResult({ id: request.id, failure: 'load' });
  }, [request.id, onResult]);
  return null;
};

/** The editor's pane: the same, with no host waiting on its draw. */
const PlanLoadFailedEditorCanvas = () => <PlanLoadFailedCanvas />;

/** The Observers corner plan: the title alone, as for every other cause. */
const PlanLoadFailedPicture = loadFailedPane(true);

/**
 * The onboarding stage when the renderer could not load (`spec-onboarding-persistent-scene-stage`,
 * R1.5): nothing. Position on map's pane above says the floor plan could not be drawn.
 */
export const PlanStage = (_props: PlanStageProps) => null;

const PlanSceneLoadFailedProviders = ({ children }: PlanSceneProvidersProps) => (
  <PlanPictureRendererContext.Provider value={PlanLoadFailedPicture}>
    <ReadOnlyCanvasRendererProvider renderer={PlanLoadFailedCanvas}>
      <PlannerCanvasRendererProvider renderer={PlanLoadFailedEditorCanvas}>
        <PlanPlacementRendererContext.Provider value={PlanLoadFailedPlacement}>
          <PlanOpeningViewRendererContext.Provider value={PlanLoadFailedOpeningViewPicture}>
            {children}
          </PlanOpeningViewRendererContext.Provider>
        </PlanPlacementRendererContext.Provider>
      </PlannerCanvasRendererProvider>
    </ReadOnlyCanvasRendererProvider>
  </PlanPictureRendererContext.Provider>
);

export default PlanSceneLoadFailedProviders;
